import type { Component } from 'solid-js'
import { createResource, createSignal, createMemo, Show } from 'solid-js'
import { useMRRSelection } from '../../shared/providers/mrr-selection.provider'
import { useUser } from '../../shared/providers/user-provider'
import { useCorisRevenue } from '../../shared/providers/coris/coris-revenue.provider'
import { DataType } from '../../features/revenue/domain/models/mrr'
import { getAvailableGroupings } from '../../features/revenue/domain/models/range'
import { computeRangeDates, getCurrentHighlightDate } from '../../shared/helpers/date.functions'
import { getFormatterForGrouping } from '../shared/charts/config/chart-formatters'
import MetricCard from '../shared/components/MetricCard'
import PeriodSelector from '../shared/components/PeriodSelector'
import GroupBySelector from '../shared/components/GroupBySelector'
import ChurnChart from '../shared/components/ChurnChart'
import ChurnTable from '../shared/components/ChurnTable'
import { sticky } from '../../shared/directives/sticky'
import { SkeletonMetricCard, SkeletonChart, SkeletonTable } from '../shared/components/Skeleton'

sticky

const ChurnOverview: Component = () => {
  const {
    selectedPeriod,
    setSelectedPeriod,
    customDateRange,
    setCustomDateRange,
    selectedGrouping,
    setSelectedGrouping
  } = useMRRSelection()
  const { organizationSlug } = useUser()
  const [isStuck, setIsStuck] = createSignal(false)

  const { getChurn } = useCorisRevenue()

  const availableGroupings = createMemo(() => getAvailableGroupings(selectedPeriod(), customDateRange()))

  const [churnData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      const period = selectedPeriod()
      const grouping = selectedGrouping()
      const custom = customDateRange()
      const { fromDate, toDate, resolution } = computeRangeDates(period, grouping, custom)
      return { fromDate, toDate, resolution, organization, grouping }
    },
    async (params) => {
      if (!params) return null

      return await getChurn.execute(
        params.fromDate,
        params.toDate,
        params.resolution,
        DataType.Users,
        params.organization
      )
    }
  )

  const processedData = createMemo(() => {
    const raw = churnData()
    if (!raw) return null
    return raw.data ?? null
  })

  const summary = createMemo(() => {
    const data = processedData()
    if (!data || data.length === 0) return null
    const totalChurn = data.reduce((sum, item) => sum + item.totalChurn, 0)
    const churnRate = data.reduce((sum, item) => sum + item.churnRate, 0)
    return { churnRate, totalChurn }
  })

  const isEconomics = () => churnData()?.dataType === DataType.Economics

  const formatCategory = createMemo(() => getFormatterForGrouping(selectedGrouping()))
  const highlightDate = createMemo(() => getCurrentHighlightDate(selectedGrouping()))

  return (
    <div class="flex flex-col gap-4 px-6">
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 py-6 transition-colors duration-200 -mx-6 px-6"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent border-b border-transparent': !isStuck()
        }}
      >
        <div class="max-w-[1280px] mx-auto w-full flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div class="flex items-center gap-3">
            <PeriodSelector
              selectedPeriod={selectedPeriod()}
              onPeriodChange={setSelectedPeriod}
              customDateRange={customDateRange()}
              onCustomRangeChange={(from, to) => setCustomDateRange({ from, to })}
              showCompareSelector={false}
            />
            <GroupBySelector
              availableGroupings={availableGroupings()}
              selectedGrouping={selectedGrouping()}
              onGroupingChange={setSelectedGrouping}
            />
          </div>
        </div>
      </div>
      <div class="max-w-[1280px] mx-auto w-full flex flex-col gap-8">
        <Show
          when={!churnData.loading && summary()}
          fallback={
            <div class="flex sm:flex-row flex-col gap-4 items-stretch">
              <SkeletonMetricCard />
              <SkeletonMetricCard />
            </div>
          }
        >
          <div class="flex sm:flex-row flex-col gap-4 items-stretch">
            <MetricCard titleKey="ls_churn_churn_rate" value={summary()!.churnRate} format="percentage" />
            <MetricCard
              titleKey="ls_churn_total_churn"
              value={summary()!.totalChurn}
              format={isEconomics() ? 'currency' : 'number'}
              dotColor="#FF6467"
            />
          </div>
        </Show>

        <Show when={!churnData.loading && processedData()} fallback={<SkeletonChart legendItems={2} />}>
          <ChurnChart data={processedData()!} formatCategory={formatCategory()} />
        </Show>

        <Show when={!churnData.loading && processedData()} fallback={<SkeletonTable columns={4} rows={5} />}>
          <ChurnTable data={processedData() ?? []} formatHeader={formatCategory()} highlightMonth={highlightDate()} />
        </Show>
      </div>
    </div>
  )
}

export default ChurnOverview
